import React, { useEffect, useMemo } from 'react'
import cx from 'classnames'
import normalizeUrl from 'normalize-url'
import { useParams } from 'react-router-dom'
import { useAppSelector } from 'reduxTyped'
import { Icon, Skeleton, l } from '@manychat/manyui'

import InstallationWizardLayoutController from 'apps/templates/components/InstallationWizardLayout'
import ProtectionWarnings from 'apps/templates/components/ProtectionWarnings'
import TemplateStructure from 'apps/templates/components/TemplateStructure'
import steps from 'apps/templates/constants/TemplateInstallationSteps'
import * as templateInstallationSelectors from 'apps/templates/selectors/templateInstallationSelectors'
import { TemplateAnalytics } from 'apps/templates/utils/TemplateAnalytics'
import { KnowledgeBaseLink } from 'common/core/components/SupportLinks'
import * as appSelectors from 'common/core/selectors/appSelectors'
import Tip from 'components/Tip'

import cm from './installationWizard.module.css'

const defaultDescription = {
  guideLink: () => KnowledgeBaseLink.HOW_TO_INSTALL_TEMPLATE,
  videoGuideLink: 'https://youtu.be/g3Qs6uT2y3I',
}

const AboutNumberedTexts = [
  {
    title: l.makeTranslate('1. Review the Template contents.'),
    description: l.makeTranslate(
      'All items will be placed in relevant sections (e.g. installed Growth Tools can be found in your Growth Tools Section). Unattached Automations will be placed in the new folder in Automations Section.',
    ),
  },
  {
    title: l.makeTranslate('2. Choose the destination Account this Template will be installed on.'),
    description: l.makeTranslate(
      'Pro Template can be installed only on a Account with Pro subscription. Free Template can be installed on any Account.',
    ),
  },
  {
    title: l.makeTranslate('3. Manage General Elements.'),
    description: l.makeTranslate(
      'In case this Template contains Main Menu, Welcome Message, or Default Reply, you’ll be able to replace your current ones automatically (your existing Automations will be stored in Automations Section). Please note that new Main Menu will replace the old one completely.',
    ),
  },
]

const Step1 = () => {
  const elements = useAppSelector(templateInstallationSelectors.getElements)
  const apps = useAppSelector(templateInstallationSelectors.getApps)
  const description = useAppSelector(templateInstallationSelectors.getDescription)
  const hasIntegrations = useAppSelector(templateInstallationSelectors.hasIntegrations)
  const templateAccessData = useAppSelector(templateInstallationSelectors.getTemplateAccessData)
  const { template_hash: templateHash } = useParams()
  const isSignIn = Boolean(useAppSelector(appSelectors.getUserId))

  useEffect(() => {
    isSignIn
      ? TemplateAnalytics.pageEvents.logReviewPageOpened({ templateHash })
      : TemplateAnalytics.pageEvents.logReviewPageLogOutOpened()
  }, [isSignIn, templateHash])

  const { summary } = description
  const guideLink = description.guideLink
    ? description.guideLink.trim()
    : defaultDescription.guideLink()
  const videoGuideLink = description.videoGuideLink
    ? description.videoGuideLink.trim()
    : defaultDescription.videoGuideLink

  const aboutTextBlock = useMemo(
    () =>
      AboutNumberedTexts.map((text, index) => {
        const title = l.getString(text.title)
        const lineBreak = index === AboutNumberedTexts.length - 1 ? null : <br />

        return (
          <React.Fragment key={title}>
            <p>{title}</p>
            <p className="m-l-0">{l.getString(text.description)}</p>

            {lineBreak}
          </React.Fragment>
        )
      }),
    [],
  )

  return (
    <InstallationWizardLayoutController
      step={steps.STEP1}
      placeholder={
        <div className="row center sm-reverse">
          <div className="m-t-xl col-12 md-col-5 lg-m-x">
            <h3>{l.translate('About')}</h3>
            <Skeleton.Text lines={5} className="m-t" />
          </div>
          <div className="m-t-xl col-12 md-col-5 lg-m-x">
            <h3>{l.translate('Template Content')}</h3>
            <div className="m-t panel panel-white panel-shadow p-a-md">
              <Skeleton.Text />
            </div>
          </div>
        </div>
      }
    >
      <div className="row center sm-reverse">
        <div className="m-t-xl col-12 md-col-5 lg-m-x">
          <h3 className="m-b">{l.translate('About')}</h3>
          <ProtectionWarnings accessData={templateAccessData} />
          {summary ? (
            <pre className={cx(cm.customSummary, 'm-t')}>{summary}</pre>
          ) : (
            <div className="m-t">{aboutTextBlock}</div>
          )}
          <a
            className="text-primary m-t d-flex align-center"
            target="_blank"
            href={normalizeUrl(guideLink)}
            rel="noreferrer"
          >
            <Icon.Info className="text-primary" />
            &nbsp; {l.translate('Read template installation guide.')}
          </a>
          <a
            className="text-primary m-t d-flex align-center"
            target="_blank"
            href={normalizeUrl(videoGuideLink)}
            rel="noreferrer"
          >
            <Icon.Video className="text-primary" />
            &nbsp; {l.translate('View template video presentation.')}
          </a>

          {hasIntegrations && (
            <Tip
              text={l.translate(
                'We’ve found an integration in this Template. Please note that you’ll need to reconnect your existing account for this particular integration on your new Page in Settings>Integrations.',
              )}
              rounded
              className={'m-t'}
            />
          )}
        </div>
        <div className="m-t-xl col-12 md-col-5 lg-m-x">
          <h3>{l.translate('Template Content')}</h3>
          <div className="m-t panel panel-white panel-shadow p-a">
            <TemplateStructure
              items={elements}
              accessData={templateAccessData}
              apps={apps}
              isTemplateInstallation
            />
          </div>
        </div>
      </div>
    </InstallationWizardLayoutController>
  )
}

/**
 * @public page component
 */
export { Step1 as Component }
